import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["mediaContainer", "currentIndex", "totalCount"]
  static values = { 
    mediaItems: Array,
    currentIndex: Number,
    favouriteFolderId: String
  }

  connect() {
    console.log("Favourites viewer modal controller connected")
    this.setupEventListeners()
    this.showCurrentItem()
  }

  disconnect() {
    // Clean up event listeners
    document.removeEventListener('keydown', this.handleKeydown.bind(this))
  }

  setupEventListeners() {
    // Keyboard navigation
    document.addEventListener('keydown', this.handleKeydown.bind(this))
    
    // Touch events for swipe navigation
    this.touchStartX = 0
    this.touchEndX = 0
    this.minSwipeDistance = 50
    this.isDragging = false

    this.mediaContainerTarget.addEventListener('touchstart', (e) => {
      if (e.target.closest('[data-action*="close"]')) return
      this.touchStartX = e.touches[0].clientX
      this.isDragging = true
    }, { passive: true })

    this.mediaContainerTarget.addEventListener('touchmove', (e) => {
      if (e.target.closest('[data-action*="close"]') || !this.isDragging) return
      this.touchEndX = e.touches[0].clientX
      const swipeDistance = this.touchEndX - this.touchStartX
      this.mediaContainerTarget.style.transform = `translateX(${swipeDistance * 0.5}px)`
    }, { passive: true })

    this.mediaContainerTarget.addEventListener('touchend', () => {
      if (!this.isDragging) return
      this.isDragging = false
      this.handleSwipeEnd()
    })
  }

  handleKeydown(e) {
    switch(e.key) {
      case 'ArrowLeft':
        this.showPrevious()
        break
      case 'ArrowRight':
        this.showNext()
        break
      case 'Escape':
        this.close()
        break
    }
  }

  handleSwipeEnd() {
    const swipeDistance = this.touchEndX - this.touchStartX
    
    if (Math.abs(swipeDistance) > this.minSwipeDistance) {
      if (swipeDistance > 0) {
        this.showPrevious()
      } else {
        this.showNext()
      }
    }
    
    // Reset transform
    this.mediaContainerTarget.style.transform = ''
  }

  showPrevious() {
    if (this.currentIndexValue > 0) {
      this.currentIndexValue--
      this.showCurrentItem()
    }
  }

  showNext() {
    if (this.currentIndexValue < this.mediaItemsValue.length - 1) {
      this.currentIndexValue++
      this.showCurrentItem()
    }
  }

  showCurrentItem() {
    const currentItem = this.mediaItemsValue[this.currentIndexValue]
    const img = this.mediaContainerTarget.querySelector('img')
    const video = this.mediaContainerTarget.querySelector('video')
    const prevButton = this.element.querySelector('[data-action*="showPrevious"]')
    const nextButton = this.element.querySelector('[data-action*="showNext"]')

    console.log('showCurrentItem called - currentItem:', currentItem, 'index:', this.currentIndexValue)

    // Reset any transform that might be left from dragging
    this.mediaContainerTarget.style.transform = ''

    // Hide both elements initially
    img.style.display = 'none'
    video.style.display = 'none'

    if (currentItem.media_type === 'image') {
      // Show image
      img.style.display = 'block'
      img.style.opacity = '0'
      setTimeout(() => {
        img.src = currentItem.file_url
        img.alt = currentItem.title
        img.style.opacity = '1'
      }, 200)
    } else if (currentItem.media_type === 'video') {
      // Show video
      video.style.display = 'block'
      video.src = currentItem.file_url
      video.load() // Force video to load new source
    }

    // Update counter
    this.currentIndexTarget.textContent = this.currentIndexValue + 1
    this.totalCountTarget.textContent = this.mediaItemsValue.length

    // Update navigation buttons
    prevButton.style.visibility = this.currentIndexValue === 0 ? 'hidden' : 'visible'
    nextButton.style.visibility = this.currentIndexValue === this.mediaItemsValue.length - 1 ? 'hidden' : 'visible'

    console.log('Media updated to:', currentItem.file_url, 'type:', currentItem.media_type)
  }

  close() {
    this.element.remove()
  }

  async removeCurrentItem() {
    const currentItem = this.mediaItemsValue[this.currentIndexValue]
    const currentUserId = document.body.dataset.currentUserId
    
    console.log('removeCurrentItem called - currentItem:', currentItem, 'index:', this.currentIndexValue)
    
    if (!confirm(`Remove "${currentItem.title}" from this favourite folder?`)) {
      return
    }
    
    try {
      const response = await fetch(`/users/${currentUserId}/favourites/remove_media_item`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
          'Accept': 'text/vnd.turbo-stream.html, application/json, text/html',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ 
          favourite_folder_id: this.favouriteFolderIdValue,
          media_item_id: currentItem.id 
        })
      })
      
      const contentType = response.headers.get('content-type')
      
      if (contentType && contentType.includes('text/vnd.turbo-stream.html')) {
        // Handle Turbo Stream response
        const html = await response.text()
        Turbo.renderStreamMessage(html)
        
        // Show success message
        this.showMessage('Item removed from folder', 'success')
        
        // Update modal state
        this.updateModalAfterRemoval(currentItem.id)
      } else {
        // Handle JSON response (fallback)
        const data = await response.json()
        console.log('Remove response:', data)
        
        if (data.success) {
          this.updateModalAfterRemoval(currentItem.id)
          this.showMessage(data.message, 'success')
        } else {
          this.showMessage(data.message, 'error')
        }
      }
    } catch (error) {
      console.error('Error removing item:', error)
      this.showMessage('Failed to remove item', 'error')
    }
  }

  updateModalAfterRemoval(removedItemId) {
    console.log('Before removal - mediaItems:', this.mediaItemsValue.length, 'currentIndex:', this.currentIndexValue)
    
    // Create a new array without the removed item
    const newMediaItems = [...this.mediaItemsValue]
    newMediaItems.splice(this.currentIndexValue, 1)
    
    // Update the media items value with the new array
    this.mediaItemsValue = newMediaItems
    
    // Update the data attribute to reflect the change
    this.element.setAttribute('data-favourites-viewer-modal-media-items-value', JSON.stringify(this.mediaItemsValue))
    
    // If no more items, close the modal
    if (this.mediaItemsValue.length === 0) {
      this.close()
      return
    }
    
    // Adjust current index if needed
    if (this.currentIndexValue >= this.mediaItemsValue.length) {
      this.currentIndexValue = this.mediaItemsValue.length - 1
    }
    
    console.log('After removal - mediaItems:', this.mediaItemsValue.length, 'currentIndex:', this.currentIndexValue)
    console.log('New current item:', this.mediaItemsValue[this.currentIndexValue])
    
    // Force a refresh of the current item display
    this.showCurrentItem()
  }

  showMessage(message, type = 'info') {
    // Create a temporary message element
    const messageEl = document.createElement('div')
    messageEl.className = 'fixed top-4 right-4 z-50 px-4 py-2 rounded-lg text-black text-sm font-medium bg-amber-50 border border-amber-200 shadow-lg'
    messageEl.textContent = message
    
    document.body.appendChild(messageEl)
    
    // Remove after 3 seconds
    setTimeout(() => {
      messageEl.remove()
    }, 3000)
  }
} 